// Utilidades de formato (fechas, precios, cuenta atrás)
import { COUNTRY_LABELS } from '../data/constants.js'

// Formatea un número como moneda EUR usando el locale alemán
// (máximo 2 decimales; se muestra como llega del auktionshaus)
export function formatPrice(value, currency = 'EUR') {
  if (value === null || value === undefined) return '–'
  const num = Number(value)
  if (Number.isNaN(num)) return '–'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 0
  }).format(num)
}

// Formatea una fecha ISO a formato legible alemán (o relativo)
export function formatDate(iso) {
  if (!iso) return '–'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '–'
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d)
}

// Diferencia en milisegundos entre ahora y un instante futuro
function diffUntil(iso) {
  const target = new Date(iso).getTime()
  return target - Date.now()
}

// Cuenta atrás simple: devuelve objeto {days, hours, mins, secs, ended}
export function countdown(iso) {
  const diff = diffUntil(iso)
  if (!iso || Number.isNaN(diff) || diff <= 0) {
    return { days: 0, hours: 0, mins: 0, secs: 0, ended: true }
  }
  const totalSecs = Math.floor(diff / 1000)
  return {
    days: Math.floor(totalSecs / 86400),
    hours: Math.floor((totalSecs % 86400) / 3600),
    mins: Math.floor((totalSecs % 3600) / 60),
    secs: totalSecs % 60,
    ended: false
  }
}

// Texto corto de cuenta atrás ("3T 04:12:33")
export function formatCountdown(iso) {
  const c = countdown(iso)
  if (c.ended) return 'Beendet'
  const hh = String(c.hours).padStart(2, '0')
  const mm = String(c.mins).padStart(2, '0')
  const ss = String(c.secs).padStart(2, '0')
  return c.days > 0 ? `${c.days}T ${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`
}

// Nombre de país a partir del código en ALEMÁN (fallback: Intl.DisplayNames de)
export function countryName(code) {
  if (!code) return 'Unbekannt'
  const key = String(code).toLowerCase()
  if (COUNTRY_LABELS[key]) return COUNTRY_LABELS[key]
  try {
    return new Intl.DisplayNames(['de'], { type: 'region' }).of(code.toUpperCase()) || code.toUpperCase()
  } catch (_) {
    return code.toUpperCase()
  }
}

// Nombre de casa de subastas legible desde el objeto source
// (el frontend no depende de esto: la API manda nombre)
export function sourceName(source) {
  if (!source) return 'Auktionshaus'
  return (source.name || source.code || 'Auktionshaus').replace(/\s+Auctions$/, '').trim()
}

// ---------------------------------------------------------------------------
// Lógica unificada de precio/etiqueta para un lote (tarjeta + detalle).
// Evita etiquetas duplicadas y precios contradictorios.
//
// Reglas (requisito de Hicham):
//  - Terminado (ended/sold/cancelled/reserve_not_met) -> "Letztes Gebot" = final_bid ?? current_bid
//  - upcoming                                        -> "Startgebot"      = start_bid
//  - Activo con pujas (bids_count>0)                 -> "Aktuelles Gebot" = current_bid (+ Startgebot secundario)
//  - Activo SIN pujas                                -> "Startgebot"      = start_bid  (NUNCA current_bid si no hay pujas)
// Devuelve { label, value, secondaryStart }
// ---------------------------------------------------------------------------
export function lotPrice(lot) {
  if (!lot) return { label: 'Startgebot', value: null, secondaryStart: null }

  const isEnded = ['ended', 'sold', 'cancelled', 'reserve_not_met'].includes(lot.status)
  const isUpcoming = lot.status === 'upcoming'
  const hasBids = (lot.bids_count || 0) > 0

  if (isEnded) {
    return {
      label: 'Letztes Gebot',
      value: lot.final_bid ?? lot.current_bid,
      secondaryStart: null
    }
  }

  if (isUpcoming) {
    return {
      label: 'Startgebot',
      value: lot.start_bid != null ? lot.start_bid : null,
      secondaryStart: null
    }
  }

  if (hasBids) {
    return {
      label: 'Aktuelles Gebot',
      value: lot.current_bid != null ? lot.current_bid : lot.start_bid,
      secondaryStart: lot.start_bid != null ? lot.start_bid : null
    }
  }

  // Activo sin pujas: SOLO Startgebot
  return {
    label: 'Startgebot',
    value: lot.start_bid != null ? lot.start_bid : null,
    secondaryStart: null
  }
}
